import { useEffect, useState } from 'react';
import 'react-perfect-scrollbar/dist/css/styles.css';

import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license';

import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { onAuthStateChanged, signOut } from 'firebase/auth';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { getUser, setUser } from '@/store/userSlice';

import Loading from './components/loading';
import { auth } from './fire';
import useAlert from './hooks/useAlert';
import Router from './router';
import {
  appInsights,
  getCurrentTraceId,
  reactPlugin
} from './telemetry/applicationInsights';
import themeDefault from './themes/themeDefault';
import Error from './views/misc/error';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: 2.5 * 60 * 1000
    }
  }
});

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY || '');

const App = () => {
  const dispatch = useAppDispatch();
  const alert = useAlert();
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Firebase Auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // User is Logged In
        dispatch(getUser()).catch(async (error) => {
          alert.error(
            `App.tsx: Failed to fetch user profile after Firebase auth: ${error}`
          );
          await signOut(auth);
        });
      } else {
        // User is Logged Out
        dispatch(setUser(null));
      }

      if (!authInitialized) {
        setAuthInitialized(true);
      }
    });

    return () => unsubscribe();
  }, [dispatch]);

  useEffect(() => {
    const userId = currentUser?.id;

    if (userId) {
      try {
        const userIdString = String(userId);
        appInsights.setAuthenticatedUserContext(userIdString, undefined, true);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          'Error setting authenticated user context for Application Insights:',
          e,
          'User ID was:',
          userId
        );
      }
    } else {
      appInsights.clearAuthenticatedUserContext();
    }
  }, [currentUser?.id]);

  return (
    <AppInsightsErrorBoundary
      onError={(error, errorInfo) => {
        appInsights.trackException({
          exception: error,
          properties: { componentStack: errorInfo?.componentStack }
        });
        const traceId = getCurrentTraceId();

        return (
          <Error
            message="An unexpected error occurred in the application"
            error={{ traceId }}
          />
        );
      }}
      appInsights={reactPlugin}
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={themeDefault}>
          <CssBaseline />
          {!authInitialized ? <Loading /> : <Router />}
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </AppInsightsErrorBoundary>
  );
};

export default App;
